import React, { useEffect, useState } from "react";
import { Card, Result, Spin, Button, Typography, Space, message } from "antd";
import { CheckCircleOutlined, HomeOutlined, FileTextOutlined, FormOutlined, DownloadOutlined } from "@ant-design/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import { WompiService } from "../../../utils/wompi/api/wompi.api";
import type { WompiApiResponse } from "../../../utils/wompi/types/types";
import { AutoFacturacionService, FacturaPreview } from '../../../api/DTEs/services/autoFacturacion.service';
import type { FacturaElectronicaResponse } from '../../../api/DTEs/types/dtes.types';
import { FacturaPreviewModal } from '../../../pages/FacturaElectronica/components/FacturaPreviewModal';
import { useAppSelector } from '../../../hooks/reduxHooks';

const { Text } = Typography;

const SuccessView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<WompiApiResponse | null>(null);
  const [preview, setPreview] = useState<FacturaPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [showManualPreview, setShowManualPreview] = useState(false);
  const [facturaGenerada, setFacturaGenerada] = useState<FacturaElectronicaResponse | null>(null);
  const [facturaDescargada, setFacturaDescargada] = useState(false);

  // Obtener datos del paciente autenticado
  const authState = useAppSelector((state) => state.auth);

  const amortizationId = searchParams.get("amortizationId");
  const transactionId = searchParams.get("transactionId") || searchParams.get("idTransaccion");
  const identificadorEnlaceComercio = searchParams.get("identificadorEnlaceComercio");
  const monto = searchParams.get("monto");
  const provider = searchParams.get("provider") || "wompi";

  // Función para descargar el PDF automáticamente
  const descargarFacturaPDF = async (enlacePdf: string, codGeneracion: string) => {
    try {
      const link = document.createElement('a');
      link.href = enlacePdf;
      link.download = `Factura-${codGeneracion}.pdf`;
      link.target = '_blank';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setFacturaDescargada(true);
      message.success('¡Factura descargada exitosamente!');
      
    } catch {
      message.warning('No se pudo descargar automáticamente. Usa el enlace para descargar manualmente.');
    }
  };

  useEffect(() => {
    const verifyPaymentAndGenerateInvoice = async () => {
      if (!amortizationId || !monto) {
        setLoading(false);
        return;
      }

      setAutoGenerating(true);
      
      try {
        // Preparar datos para facturación automática
        const amort = {
          amortizationId: parseInt(amortizationId),
          treatmentPlanId: 0,
          paymentDate: new Date().toISOString(),
          amount: monto ? parseFloat(monto) : 0,
          status: 'paid' as const
        };
        
        // Usar datos reales del paciente autenticado
        const pacienteData = authState.pacienteData || {
          pacienteId: 0,
          userId: null,
          codigoPaciente: amortizationId || '',
          nombre1: 'Cliente',
          apellido1: 'Anónimo',
          fechaNac: '',
          sexo: '',
          estadoCivil: '',
          direccion: 'San Salvador, El Salvador',
          departamento: 6,
          municipio: 14,
          distrito: 1,
          dui: '',
          codigoArea: '06',
          telefono: '',
          email: ''
        };

        const autoFacturacionData = {
          amortization: amort,
          paciente: pacienteData,
          transactionId: transactionId || undefined,
          paymentDate: new Date().toISOString(),
          provider: provider
        };

        const facturaResult = await AutoFacturacionService.generarFacturaAutomatica(autoFacturacionData, false);
        
        if ('codGeneracion' in facturaResult) {
          const facturaResponse = facturaResult as FacturaElectronicaResponse;
          setFacturaGenerada(facturaResponse);
          
          message.success(`¡Factura electrónica generada exitosamente! Código: ${facturaResponse.codGeneracion}`);
          
          if (facturaResponse.enlace_pdf) {
            await descargarFacturaPDF(facturaResponse.enlace_pdf, facturaResponse.codGeneracion);
          }
          
        } else {
          message.error('Error: Respuesta de facturación inválida');
        }
        
      } catch {
        message.error('No se pudo generar la factura automáticamente. Puedes generarla manualmente más tarde.');
      } finally {
        setAutoGenerating(false);
      }

      try {
        const status = await WompiService.getPaymentStatus(parseInt(amortizationId));
        setPaymentInfo(status);
      } catch {
        // Error silencioso en verificación de pago
      } finally {
        setLoading(false);
      }
    };

    verifyPaymentAndGenerateInvoice();
  }, [amortizationId, transactionId, identificadorEnlaceComercio, monto, provider, authState.pacienteData]);

  const handleGoHome = () => {
    navigate("/perfil");
  };

  const handleEmitirFactura = () => {
    if (!amortizationId) return;
    navigate(
      `/factura-electronica?amortizationId=${amortizationId}&transactionId=${transactionId}&monto=${monto}&provider=${provider}`
    );
  };

  const handleEmitirFacturaManualConPreview = async () => {
    if (!amortizationId || !transactionId || !monto) {
      message.error('Faltan datos del pago para generar el preview');
      return;
    }

    setPreviewLoading(true);
    try {
      // Crear datos básicos para el preview
      const amortizationData = {
        amortizationId: parseInt(amortizationId),
        treatmentPlanId: 0,
        paymentDate: new Date().toISOString(),
        amount: parseFloat(monto),
        status: 'paid' as const,
        treatmentName: `Servicio Dental - Amortización #${amortizationId}`
      };

      // Usar datos reales del paciente autenticado
      const pacienteData = {
        pacienteId: authState.pacienteData?.pacienteId || 0,
        userId: authState.userData?.userId || null,
        codigoPaciente: authState.pacienteData?.codigoPaciente || amortizationId,
        nombre1: authState.pacienteData?.nombre1 || 'Cliente',
        apellido1: authState.pacienteData?.apellido1 || 'Genérico',
        fechaNac: authState.pacienteData?.fechaNac || '1990-01-01',
        sexo: authState.pacienteData?.sexo || 'M',
        estadoCivil: authState.pacienteData?.estadoCivil || 'Soltero',
        direccion: authState.pacienteData?.direccion || 'San Salvador',
        departamento: authState.pacienteData?.departamento || 6,
        municipio: authState.pacienteData?.municipio || 14,
        distrito: authState.pacienteData?.distrito || 1,
        telefono: authState.pacienteData?.telefono || '0000-0000',
        email: authState.pacienteData?.email || 'cliente@email.com',
        dui: authState.pacienteData?.dui && authState.pacienteData.dui !== '' && authState.pacienteData.dui !== null
          ? authState.pacienteData.dui
          : '00000000-0',
        codigoArea: authState.pacienteData?.codigoArea || '06'
      };

      const autoFacturacionData = {
        amortization: amortizationData,
        paciente: pacienteData
      };

      // Generar preview
      const result = await AutoFacturacionService.generarFacturaAutomatica(autoFacturacionData, true);
      setPreview(result as FacturaPreview);
      setShowManualPreview(true);
      
    } catch {
      message.error('Error al generar el preview. Usando formulario manual.');
      handleEmitirFactura();
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmManualWithPreview = () => {
    setShowManualPreview(false);
    setPreview(null);
    handleEmitirFactura();
  };

  const handleEmitirFacturaAutomatica = async () => {
    if (!amortizationId || !transactionId || !monto) {
      message.error('Faltan datos del pago para generar la factura automáticamente');
      return;
    }

    setAutoGenerating(true);
    try {
      // Crear datos básicos para la factura automática
      const amortizationData = {
        amortizationId: parseInt(amortizationId),
        treatmentPlanId: 0,
        paymentDate: new Date().toISOString(),
        amount: parseFloat(monto),
        status: 'paid' as const,
        treatmentName: `Servicio Dental - Amortización #${amortizationId}`
      };

      // Usar datos reales del paciente autenticado para la factura automática
      const pacienteData = {
        pacienteId: authState.pacienteData?.pacienteId || 0,
        userId: authState.userData?.userId || null,
        codigoPaciente: authState.pacienteData?.codigoPaciente || amortizationId,
        nombre1: authState.pacienteData?.nombre1 || 'Cliente',
        apellido1: authState.pacienteData?.apellido1 || 'Genérico',
        fechaNac: authState.pacienteData?.fechaNac || '1990-01-01',
        sexo: authState.pacienteData?.sexo || 'M',
        estadoCivil: authState.pacienteData?.estadoCivil || 'Soltero',
        direccion: authState.pacienteData?.direccion || 'San Salvador',
        departamento: authState.pacienteData?.departamento || 6,
        municipio: authState.pacienteData?.municipio || 14,
        distrito: authState.pacienteData?.distrito || 1,
        telefono: authState.pacienteData?.telefono || '0000-0000',
        email: authState.pacienteData?.email || 'cliente@email.com',
        dui: authState.pacienteData?.dui && authState.pacienteData.dui !== '' && authState.pacienteData.dui !== null
          ? authState.pacienteData.dui
          : '00000000-0',
        codigoArea: authState.pacienteData?.codigoArea || '06'
      };

      const autoFacturacionData = {
        amortization: amortizationData,
        paciente: pacienteData
      };

      // Generar factura automáticamente (no preview)
      const result = await AutoFacturacionService.generarFacturaAutomatica(autoFacturacionData, false);
      
      if ('success' in result && result.success) {
        message.success('¡Factura electrónica generada exitosamente!');
      } else {
        throw new Error('Error en la generación de factura');
      }
      
    } catch {
      message.error('Error al generar la factura automáticamente. Use el botón "Emitir Factura Manual" para completar los datos.');
    } finally {
      setAutoGenerating(false);
    }
  };

  const handleViewAccount = () => {
    navigate(`/perfil`);
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "400px" 
      }}>
        <Card style={{ textAlign: "center", minWidth: "300px" }}>
          <Spin size="large" tip="Verificando pago..." />
        </Card>
      </div>
    );
  }

  return (
    <>
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
        padding: "20px"
      }}>
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            status="success"
            title="¡Pago Exitoso!"
            subTitle={
              <Space direction="vertical" size="small">
                <Text>Tu pago ha sido procesado correctamente.</Text>
                {amortizationId && (
                  <Text type="secondary">
                    Amortización #{amortizationId}
                  </Text>
                )}
                {transactionId && (
                  <Text type="secondary">
                    ID de Transacción: {transactionId}
                  </Text>
                )}
                {paymentInfo?.message && (
                  <Text type="secondary">
                    {paymentInfo.message}
                  </Text>
                )}
                
                {/* Mostrar información de la factura generada */}
                {facturaGenerada && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', border: '1px solid #52c41a', borderRadius: 6 }}>
                    <Text style={{ fontSize: '14px', color: '#389e0d' }}>
                      <strong>Factura Electrónica Generada:</strong><br/>
                      Código: <strong>{facturaGenerada.codGeneracion}</strong><br/>
                      Estado: <strong>{facturaGenerada.estado}</strong><br/>
                      {facturaDescargada && (
                        <span style={{ color: '#52c41a' }}>
                          <strong>PDF descargado exitosamente</strong>
                        </span>
                      )}
                      {!facturaDescargada && facturaGenerada.enlace_pdf && (
                        <a 
                          href={facturaGenerada.enlace_pdf} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#1890ff', textDecoration: 'underline' }}
                        >
                          Descargar PDF manualmente
                        </a>
                      )}
                    </Text>
                  </div>
                )}
                
                {/* Mostrar indicador de generación automática */}
                {autoGenerating && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#e6f7ff', border: '1px solid #1890ff', borderRadius: 6 }}>
                    <Text style={{ fontSize: '14px', color: '#1890ff' }}>
                      <strong>Generando factura automáticamente...</strong><br/>
                      Por favor espera mientras se procesa tu factura electrónica.
                    </Text>
                  </div>
                )}
                
                {!facturaGenerada && !autoGenerating && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fffbe6', border: '1px solid #d4b106', borderRadius: 6 }}>
                    <Text style={{ fontSize: '12px', color: '#d4b106' }}>
                      <strong>Aviso:</strong> La factura se genera automáticamente al llegar a esta página.<br/>
                      Si no se generó automáticamente, puedes usar los botones de abajo.
                    </Text>
                  </div>
                )}
              </Space>
            }
            extra={[
              <Button 
                type="primary" 
                key="account" 
                onClick={handleViewAccount}
                style={{ marginRight: "8px" }}
              >
                Ver Estado de Cuenta
              </Button>,
              <Button 
                key="home" 
                icon={<HomeOutlined />} 
                onClick={handleGoHome}
                style={{ marginRight: "8px" }}
              >
                Ir al Inicio
              </Button>,
              
              // Mostrar botón de descarga si la factura fue generada pero no descargada
              ...(facturaGenerada && !facturaDescargada && facturaGenerada.enlace_pdf ? [
                <Button 
                  key="download-pdf" 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={() => descargarFacturaPDF(facturaGenerada.enlace_pdf!, facturaGenerada.codGeneracion)}
                  style={{ marginRight: "8px", backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Descargar Factura PDF
                </Button>
              ] : []),
              
              // Solo mostrar botones de facturación manual si no se generó automáticamente y no está generando
              ...((!facturaGenerada && !autoGenerating) ? [
                <Button 
                  key="emitir-manual" 
                  icon={<FormOutlined />}
                  onClick={handleEmitirFacturaManualConPreview}
                  loading={previewLoading}
                  style={{ marginRight: "8px" }}
                >
                  Emitir Factura Manual
                </Button>,
                <Button 
                  key="retry-auto" 
                  type="primary" 
                  icon={<FileTextOutlined />}
                  onClick={handleEmitirFacturaAutomatica}
                  style={{ marginRight: "8px" }}
                >
                  Reintentar Factura Automática
                </Button>
              ] : [])
            ]}
          />
        </Card>
      </div>
      {/* Modal de vista previa de factura */}
      {preview && showManualPreview && (
        <FacturaPreviewModal
          visible={showManualPreview}
          preview={preview}
          onClose={() => {
            setShowManualPreview(false);
            setPreview(null);
          }}
          onGenerate={handleConfirmManualWithPreview}
          loading={previewLoading}
        />
      )}
      {/* Modal de vista previa automática */}
      {preview && !showManualPreview && (
        <FacturaPreviewModal
          visible
          preview={preview}
          onClose={() => setPreview(null)}
          loading={previewLoading}
        />
      )}
    </>
  );
};

export default SuccessView;